import { useMemo, useState } from 'react'
import { CalendarDays, Check, Wrench } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { Booking } from '../data/model'
import { addDays, nights, todayISO } from '../lib/dates'
import type { EnquiryDraft } from '../lib/enquiryDraft'
import { Button, Field, Input, Sheet, cx } from '../ui/kit'
import EnquiryPasteCard from './EnquiryPasteCard'

/**
 * Was aus einer eingefügten Anfrage über den Gast bekannt ist.
 *
 * Reicht bis zum Buchungsblatt durch, statt hier schon einen Gast anzulegen: ein
 * Gastdatensatz, der entsteht, bevor der Vermieter die Buchung überhaupt gesehen
 * hat, bleibt als Karteileiche zurück, wenn er das Blatt wieder zumacht.
 */
export interface BookingPrefill {
  first_name: string
  last_name: string
  email: string
  phone: string
}

/**
 * "A guest wants these nights — what have I got?"
 *
 * On a phone the + button used to drop the landlord into the first unit in the list
 * on today's date, which is almost never the booking they were about to take. Asking
 * for the dates first turns the same button into the question they were actually
 * answering, and the unit list then shows what is free rather than everything.
 *
 * Occupied units are listed too, greyed out and naming whoever has them. Hiding them
 * would look like the unit did not exist; saying who is in it is the difference
 * between "no" and "no, because".
 *
 * Und weil genau diese Fragen — welche Nächte, wie viele Personen — die sind, die in
 * einer Anfrage per Mail oder WhatsApp stehen, sitzt „Anfrage einfügen" hier oben
 * drüber: auf dem Weg, den der Vermieter mit der Anfrage in der Hand ohnehin geht.
 */
export default function NewBookingSheet({
  open, onClose, onPick,
}: {
  open: boolean
  onClose: () => void
  /** Called with a draft booking for the chosen unit and dates. */
  onPick: (draft: Booking, prefill?: BookingPrefill) => void
}) {
  const { t, lang, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [arrival, setArrival] = useState(todayISO)
  const [departure, setDeparture] = useState(() => addDays(todayISO(), 1))
  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  /** Nur vollständig oder leer — halbe Altersangaben ergäben eine halbe Ermäßigung. */
  const [childrenAges, setChildrenAges] = useState<number[]>([])
  /** Was die eingefügte Anfrage über den Gast hergab; wandert ins Buchungsblatt. */
  const [prefill, setPrefill] = useState<BookingPrefill | undefined>()
  /** Wünsche und Haustier aus der Anfrage, für die interne Notiz der Buchung. */
  const [notes, setNotes] = useState('')
  /**
   * A block is the same question — these dates, that unit — with nobody staying and
   * nothing to charge. It shares this sheet rather than hiding behind a second
   * button, because until now the calendar could show a Sperre but not create one.
   */
  const [mode, setMode] = useState<'booking' | 'block'>('booking')
  const isBlock = mode === 'block'

  const stayNights = nights(arrival, departure)

  const units = useMemo(
    () => store.db.units.filter((u) => u.active), [store.db.units])

  const rows = useMemo(() => units.map((unit) => {
    const blockers = store
      .overlappingBookings(unit.id, arrival, departure)
      .filter((b) => b.kind === 'block' || !['enquiry', 'quoted'].includes(b.status))
    const priced = stayNights > 0
      ? store.price({
        unit_id: unit.id, arrival, departure,
        adults, children, children_ages: childrenAges,
      })
      : null
    return { unit, blockers, priced, free: blockers.length === 0 }
  }), [units, store, arrival, departure, adults, children, childrenAges, stayNights])

  const free = rows.filter((r) => r.free)
  const taken = rows.filter((r) => !r.free)

  /**
   * Den erkannten Entwurf in die Felder dieses Blattes übernehmen.
   *
   * Nur was erkannt wurde. Ein `null` aus der Auswertung heißt „stand nicht im
   * Text" und darf den Wert, der hier schon steht, nicht auf einen erfundenen
   * zurücksetzen — deshalb wird jedes Feld einzeln geprüft und nicht der ganze
   * Satz übergebügelt.
   */
  const applyEnquiry = (d: EnquiryDraft) => {
    if (d.arrival) {
      setArrival(d.arrival)
      // Dieselbe Regel wie am Datumsfeld: die Abreise darf nicht hinter die
      // Anreise fallen. Das ist die Zwangsbedingung des Formulars, keine
      // Aussage über den Aufenthalt — der Vermieter sieht die Nacht und ändert sie.
      if (d.arrival >= departure) setDeparture(addDays(d.arrival, 1))
    }
    if (d.departure && d.departure > (d.arrival ?? arrival)) setDeparture(d.departure)
    if (d.adults !== null) setAdults(d.adults)
    if (d.children !== null) setChildren(d.children)
    setChildrenAges(d.childrenAges)

    if (d.firstName || d.lastName || d.email || d.phone) {
      setPrefill({
        first_name: d.firstName, last_name: d.lastName,
        email: d.email, phone: d.phone,
      })
    }

    /*
     * Wünsche und Haustier landen in der internen Notiz, nicht in einer
     * Nebenkostenzeile. Ein Hund kostet in den meisten Betrieben etwas, aber
     * optionale Nebenkosten werden in diesem Produkt nie von selbst berechnet
     * (siehe `pricing.ts`) — der Vermieter hakt sie im Buchungsblatt an, und die
     * Notiz ist das, was ihn daran erinnert.
     */
    const parts = [
      d.wishes,
      d.pet === true ? (de ? 'Haustier angekündigt' : 'Pet announced') : '',
    ].filter(Boolean)
    setNotes(parts.join(' · '))
  }

  /** Demo mode has no database trigger to number a block, so do it here. */
  const blockReference = () => {
    const year = todayISO().slice(0, 4)
    const n = store.db.bookings.filter((b) => b.kind === 'block').length + 1
    return `S-${year}-${String(n).padStart(4, '0')}`
  }

  const pick = (unitId: string) => {
    onPick({
      id: crypto.randomUUID(),
      tenant_id: store.db.tenant.id,
      unit_id: unitId,
      guest_id: null,
      reference: store.live ? '' : (isBlock ? blockReference() : store.nextReference('booking')),
      kind: isBlock ? 'block' : 'booking',
      /*
       * Eine frisch aufgezogene Buchung ist eine OPTION, keine Bestätigung.
       *
       * Sie stand hier auf „bestätigt", damit der Balken im Kalender überhaupt
       * erscheint — blockierende Status sind die, die gezeichnet werden. Das war der
       * richtige Reflex für ein falsches Problem: eine Buchung ohne Namen, ohne
       * Anzahlung und ohne Zusage kann nicht bestätigt sein, und wer sie so vorfindet,
       * glaubt dem Kalender nicht mehr.
       *
       * Die Option blockiert die Einheit ebenfalls — es kann also niemand
       * dazwischenbuchen — sagt aber die Wahrheit über den Stand. Und sie läuft von
       * selbst ab (`option_until`), statt eine Woche später als feste Buchung
       * dazustehen, an die sich niemand erinnert.
       *
       * Eine Sperre bleibt bestätigt: sie ist eine Tatsache über das Objekt und
       * wartet auf niemanden.
       */
      status: isBlock ? 'confirmed' : 'option',
      channel: 'direct',
      arrival,
      departure,
      adults: isBlock ? 0 : adults,
      children: isBlock ? 0 : children,
      children_ages: isBlock ? [] : childrenAges,
      optional_fee_ids: [],
      currency: store.db.tenant.currency,
      total_net: 0, total_vat: 0, total_gross: 0,
      prepayment_amount: 0, prepayment_due: null,
      deposit_amount: 0, deposit_status: 'not_required',
      internal_notes: isBlock ? '' : notes,
      created_at: new Date().toISOString(),
    }, isBlock ? undefined : prefill)
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {isBlock ? <Wrench size={18} /> : <CalendarDays size={18} />}
          {isBlock ? t('status.block') : t('calendar.newBooking')}
        </span>
      }
    >
      <div className="flex gap-1 p-1 rounded-xl bg-surface-2" role="group">
        {(['booking', 'block'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cx('flex-1 py-1.5 rounded-lg text-sm font-medium',
              mode === m ? 'bg-accent text-accent-fg' : 'text-muted')}
          >
            {m === 'booking' ? t('calendar.newBooking') : t('status.block')}
          </button>
        ))}
      </div>

      {/* Eine Sperre hat keinen Gast und keine Anfrage — dort wäre der Knopf nur
          eine Ablenkung von der einen Frage, die eine Sperre stellt. */}
      {!isBlock && <EnquiryPasteCard onApply={applyEnquiry} />}

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('booking.arrival')}>
          <Input
            type="date" value={arrival}
            onChange={(e) => {
              const v = e.target.value
              setArrival(v)
              // Keep the stay at least one night rather than letting the departure
              // silently fall behind the arrival.
              if (v >= departure) setDeparture(addDays(v, 1))
            }}
          />
        </Field>
        <Field label={t('booking.departure')}>
          <Input
            type="date" value={departure} min={addDays(arrival, 1)}
            onChange={(e) => setDeparture(e.target.value)}
          />
        </Field>
      </div>

      {!isBlock && (
      <>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('common.adults')}>
          <Input
            type="number" min={1} max={20} value={adults}
            onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))}
          />
        </Field>
        {/*
          Kinder stehen jetzt hier, weil die eingefügte Anfrage sie mitbringt: sie
          wandern sonst zwar in den Entwurf, aber der Vermieter sähe im Preis
          neben der Einheit eine Zahl, die er sich nicht erklären kann. Und sie
          ändern den Preis — Kinderermäßigung und Ortstaxe hängen daran.
        */}
        <Field label={t('common.children')}>
          <Input
            type="number" min={0} max={15} value={children}
            onChange={(e) => {
              const n = Math.max(0, Number(e.target.value) || 0)
              setChildren(n)
              // Alter, die nicht mehr zur Anzahl passen, sind keine halbe Angabe,
              // sondern gar keine — dieselbe Regel wie bei der Auswertung.
              setChildrenAges((ages) => (ages.length === n ? ages : []))
            }}
          />
        </Field>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted">
        <span>
          {stayNights > 0
            ? `${stayNights} ${stayNights === 1 ? t('common.night') : t('common.nights')}`
            : (de ? 'Abreise nach Anreise wählen' : 'Departure must follow arrival')}
        </span>
        {childrenAges.length > 0 && (
          <span>
            · {de ? 'Alter' : 'Ages'} {childrenAges.join(', ')}
          </span>
        )}
      </div>
      </>
      )}

      <h3 className="text-sm font-medium mt-1">
        {isBlock
          ? (de ? 'Einheit sperren' : 'Block a unit')
          : (de ? 'Freie Einheiten' : 'Available units')}
        <span className="text-muted font-normal"> ({free.length}/{rows.length})</span>
      </h3>

      {free.length === 0 && (
        <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
          {de
            ? 'In diesem Zeitraum ist nichts frei.'
            : 'Nothing is free for those dates.'}
        </p>
      )}

      <div className="space-y-2">
        {free.map(({ unit, priced }) => (
          <button
            key={unit.id}
            onClick={() => pick(unit.id)}
            className="w-full text-left rounded-2xl border border-border bg-surface-2
                       p-3 flex items-center gap-3 active:bg-surface"
          >
            <span className="w-1.5 h-9 rounded-full shrink-0" style={{ background: unit.color }} />
            <span className="min-w-0 flex-1">
              <span className="block font-medium truncate">{unit.name}</span>
              <span className="block text-xs text-muted">
                {de ? 'bis' : 'up to'} {unit.max_occupancy} {t('booking.occupancy')}
              </span>
            </span>
            {priced && !isBlock && (
              <span className="text-sm tabular-nums font-medium">
                {new Intl.NumberFormat(de ? 'de-AT' : 'en-GB',
                  { style: 'currency', currency: store.db.tenant.currency })
                  .format(priced.totals.gross)}
              </span>
            )}
            <Check size={16} className="text-ok shrink-0" />
          </button>
        ))}

        {taken.map(({ unit, blockers }) => (
          <div
            key={unit.id}
            className={cx('w-full rounded-2xl border border-border p-3 flex items-center gap-3',
              'opacity-55')}
          >
            <span className="w-1.5 h-9 rounded-full shrink-0" style={{ background: unit.color }} />
            <span className="min-w-0 flex-1">
              <span className="block font-medium truncate">{unit.name}</span>
              <span className="block text-xs text-muted truncate">
                {blockers.map((b) => b.kind === 'block'
                  ? (b.internal_notes || t('status.block'))
                  : `${store.guestName(b.guest_id)} · ${formatDate(b.arrival)}–${formatDate(b.departure)}`)
                  .join(' · ')}
              </span>
            </span>
          </div>
        ))}
      </div>

      <Button variant="secondary" className="w-full mt-2" onClick={onClose}>
        {t('common.cancel')}
      </Button>
    </Sheet>
  )
}
